/** @format */

import { CommonModule, DOCUMENT, isPlatformServer, Location } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router, RoutesRecognized } from '@angular/router';
import { Chain, Environment } from '@shared/data-access/interfaces';
import { filter, map, switchMap } from 'rxjs/operators';
import { CeopsService } from '../services/ceops.service';
import { UtilityService } from '@shared/data-access/services';
import { Subscription } from 'rxjs';

@Component({
	selector: 'shared-ceops',
	imports: [CommonModule],
	templateUrl: './ceops.component.html',
	styleUrl: './ceops.component.scss',
	standalone: true
})
export class CeopsComponent implements OnInit, OnDestroy {
	/**
	 * @description
	 * Feature flag to enable server-side caching of the CEOPS script using Transfer State.
	 */
	useCachedCeopsScripts = false;
	socialCommerceFlow = false;
	arrayAsyncScripts = [];
	subscription = new Subscription();

	constructor(
		private ceopsService: CeopsService,
		public chain: Chain,
		private router: Router,
		private readonly environment: Environment,
		private readonly utilityService: UtilityService,
		private readonly location: Location,
		@Inject(DOCUMENT) private readonly document: Document,
		@Inject(PLATFORM_ID) private readonly platformId
	) {}

	ngOnInit(): void {
		this.useCachedCeopsScripts = this.utilityService.checkKillSwitch(
			'useCachedCeopsScripts',
			this.environment.killSwitch,
			this.chain.chainIdentifierAbbr
		);

		this.socialCommerceFlow = this.utilityService.checkKillSwitch(
			'socialCommerceFlow',
			this.environment.killSwitch,
			this.chain.chainIdentifierAbbr
		);

		// SRLP is using withEnabledBlockingInitialNavigation in the Router provider which prevents
		// earlier events like RoutesRecognized from firing during app bootstrap.
		const useNavigationEnd = this.location.path().startsWith('/search/');

		this.subscription.add(
			this.router.events
				.pipe(
					filter((event) =>
						useNavigationEnd
							? event instanceof NavigationEnd
							: event instanceof RoutesRecognized
					),
					map((route: NavigationEnd | RoutesRecognized) => route.url.split('?')[0]),
					switchMap((url) =>
						this.ceopsService.getScripts$(
							url,
							this.chain.chainMarketingIdentifierAbbr.toUpperCase(),
							3,
							this.useCachedCeopsScripts
						)
					)
				)
				.subscribe((response) => {
					const filteredResponse = this.filterCEOPSContent(response);
					this.handleCEOPSContent(filteredResponse);
				})
		);
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	public filterCEOPSContent(response) {
		if (response.scripts.length <= 0) {
			return response;
		}

		if (this.socialCommerceFlow) {
			const excludedPaths = ['/one-offs/live-chat-link', '/live-chat/index'];
			response.scripts = response.scripts.filter(
				(script) => !excludedPaths.includes(script.path)
			);
		}
		return response;
	}

	public handleCEOPSContent(response) {
		if (response.scripts.length <= 0) {
			return;
		}

		const fragment = this.document.createDocumentFragment();
		const fragmentPreloads = this.document.createDocumentFragment();
		const isPlatformServerValue = isPlatformServer(this.platformId);
		const currentScripts = new Set<string>();

		if (!isPlatformServerValue) {
			const selector = `script[src*="${this.ceopsService.API_URL}"]`;
			const scriptTags = this.document.querySelectorAll<HTMLScriptElement>(selector);
			for (const { src } of scriptTags) {
				if (src) {
					currentScripts.add(this.normalizeUrl(src));
				}
			}
		}

		response.scripts.forEach((script) => {
			if (!script.path) {
				return;
			}
			const id = `ceopsScript_${script.path.replace(/\//g, '__')}`;

			if (!isPlatformServerValue && !currentScripts.has(this.normalizeUrl(script.src))) {
				this.createScriptTag(fragment, script, id);
			} else if (isPlatformServerValue && script.loadSynchronously) {
				// Script tag
				this.createScriptTag(fragment, script, id);

				// Preload link tag
				const linkEl = this.document.createElement('link');
				linkEl.setAttribute('rel', 'preload');
				linkEl.setAttribute('href', script.src);
				linkEl.setAttribute('as', 'script');
				linkEl.setAttribute('fetchpriority', 'high');
				fragmentPreloads.appendChild(linkEl);
			}
		});
		if (isPlatformServerValue) {
			this.document.head.appendChild(fragmentPreloads);
		}
		this.document.head.appendChild(fragment);
		setTimeout(() => {
			if (this.arrayAsyncScripts.length > 0) {
				const fragment = this.document.createDocumentFragment();
				this.arrayAsyncScripts.forEach((element) => {
					fragment.appendChild(element);
				});
				this.document.head.appendChild(fragment);
			}
		}, 0);
	}

	private createScriptTag(fragment: DocumentFragment, script, id: string) {
		const newEl = this.document.createElement('script');
		newEl.src = script.src;
		newEl.type = 'application/javascript';
		newEl.id = id;

		if (!script.loadSynchronously) {
			newEl.async = true;
			this.arrayAsyncScripts.push(newEl);
		} else {
			fragment.appendChild(newEl);
		}
	}

	private normalizeUrl(urlString: string): string {
		const u = new URL(urlString);
		let path = u.pathname;
		if (path.length > 1 && path.endsWith('/')) {
			path = path.slice(0, -1);
		}
		return u.origin + path;
	}
}
